import { useEffect, useState, useMemo } from 'react';
import { adminExpenseApi, adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import Input from '../../components/ui/Input.jsx';
import Button from '../../components/ui/Button.jsx';
import Badge from '../../components/ui/Badge.jsx';
import Modal from '../../components/ui/Modal.jsx';
import {
  Download,
  FileSpreadsheet,
  Archive,
  Settings2,
  Save,
  Trash2,
  RotateCcw,
  AlertTriangle,
  Loader2,
  BadgeCheck,
  Coins,
  Pencil,
  Plus,
  X,
} from 'lucide-react';
import ExpenseTemplatePage from './ExpenseTemplatePage.jsx';
import ReceiptViewer from '../../components/expense/ReceiptViewer.jsx';
import CurrencyCombobox from '../../components/expense/CurrencyCombobox.jsx';
import { buildSingleReportBundle, buildBulkBundle, downloadBlob } from '../../lib/expenseExport.js';
import { cn } from '@/lib/utils';

// Compact FX note shown under the SAR total for foreign-currency line
// items — just the rate + the flat 3% fee, highlighted, instead of
// spelling out the full base/fee/total arithmetic. Keyed off the stored
// fee (not currencyType !== 'SAR') so this stays correct regardless of the
// line owner's home currency — the fee is already baked into
// foreignFeeSar by the time it's saved. Falls back to the old SAR check
// for legacy rows saved before foreignFeeSar existed.
function fxNote(item) {
  const isForeign = item.foreignFeeSar != null
    ? item.foreignFeeSar > 0
    : !!item.currencyType && item.currencyType !== 'SAR';
  if (!isForeign) return null;
  const rate = Number(item.exchangeRate || 1);
  return `@ ${rate.toFixed(4)} · +3%`;
}

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'paid', label: 'Paid' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_VARIANT = {
  pending: 'warning',
  approved: 'success',
  paid: 'info',
  rejected: 'danger',
  draft: 'default',
  cancelled: 'default',
};

const STATUS_OPTIONS = ['draft', 'pending', 'approved', 'paid', 'rejected', 'cancelled'];

export default function AdminExpenseRequestsPage() {
  const toast = useToast();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [exportingId, setExportingId] = useState(null);
  const [recomputingFees, setRecomputingFees] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showTemplateConfig, setShowTemplateConfig] = useState(false);

  // Admin edit form mirrors `selected` but is locally mutable until Save.
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Full report payload (line items + receipts with viewer URLs). Loaded
  // lazily on row click so the list stays cheap.
  const [fullReport, setFullReport] = useState(null);
  const [loadingFull, setLoadingFull] = useState(false);
  // Line-item edit mode. null = read-only table; an array = editable rows
  // (a mutable copy of fullReport.lineItems, carrying every field so hidden
  // ones like itemKey/companyCardUsed survive the round-trip).
  const [editLines, setEditLines] = useState(null);
  const [savingLines, setSavingLines] = useState(false);

  const refresh = () => {
    setLoading(true);
    return adminExpenseApi.listReports()
      .then((res) => setReports(res.reports || []))
      .catch((err) => toast.error(`Failed to load reports: ${err.message}`))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When a report is selected: seed the edit form, reset confirm state,
  // and pull the full payload (line items + receipts with viewer URLs).
  useEffect(() => {
    if (selected) {
      setEdit({
        claimRef: selected.claimRef || '',
        claimPurpose: selected.claimPurpose || '',
        officeLocation: selected.officeLocation || '',
        position: selected.position || '',
        project: selected.project || '',
        client: selected.client || '',
        totalClaimedSAR: selected.totalClaimedSAR ?? 0,
        lessAdvancesPaid: selected.lessAdvancesPaid ?? 0,
        status: selected.status || 'pending',
      });
      setConfirmDelete(false);
      setEditLines(null);
      setFullReport(null);
      setLoadingFull(true);
      adminExpenseApi.getExpenseReport(selected.partitionKey, selected.rowKey)
        .then((res) => setFullReport(res))
        .catch((err) => toast.error(`Failed to load receipts/line items: ${err.message}`))
        .finally(() => setLoadingFull(false));
    } else {
      setEdit(null);
      setFullReport(null);
    }
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    let list = reports;
    if (statusFilter !== 'all') list = list.filter((r) => r.status === statusFilter);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((r) =>
        r.claimRef?.toLowerCase().includes(s) ||
        r.claimPurpose?.toLowerCase().includes(s) ||
        r.requesterName?.toLowerCase().includes(s) ||
        r.requesterEmail?.toLowerCase().includes(s)
      );
    }
    return list;
  }, [reports, statusFilter, search]);

  const pendingCount = reports.filter((r) => r.status === 'pending').length;

  // Single-report export: ZIP containing the XLSX + a receipts/ folder
  // with the raw uploaded files. Falls back to receipts-less XLSX if the
  // report has no attachments.
  const handleExportBundle = async (report) => {
    setExportingId(report.rowKey);
    const tid = toast.loading('Preparing export — building XLSX + receipts…');
    try {
      const { blob, fileName } = await buildSingleReportBundle({
        userId: report.partitionKey,
        reportId: report.rowKey,
        displayName: report.requesterName,
        claimRef: report.claimRef,
      }, { onWarn: (m) => console.warn('[expense-bundle]', m) });
      downloadBlob(blob, fileName);
      toast.success('Export ready — downloading', { id: tid });
    } catch (err) {
      toast.error(`Export failed: ${err.message}`, { id: tid });
    } finally {
      setExportingId(null);
    }
  };

  // Bulk export: parent ZIP, one folder per report, each folder
  // containing its XLSX + receipts/ subfolder.
  const handleBulkExport = async () => {
    const toExport = filtered.filter((r) => r.rowKey);
    if (toExport.length === 0) {
      toast.error('No reports to export');
      return;
    }

    setExporting(true);
    const tid = toast.loading(`Preparing export… 0 of ${toExport.length}`);
    try {
      const { blob, fileName, included } = await buildBulkBundle(toExport, {
        onWarn: (m) => console.warn('[expense-bulk]', m),
        onProgress: ({ current, total }) => toast.loading(`Exporting ${current} of ${total}…`, { id: tid }),
      });
      if (!blob) {
        toast.error('No reports could be exported. Make sure the export template is configured.', { id: tid });
        return;
      }
      downloadBlob(blob, fileName);
      toast.success(`Exported ${included} report${included !== 1 ? 's' : ''} (XLSX + receipts) as ZIP`, { id: tid });
    } catch (err) {
      toast.error(err.message, { id: tid });
    } finally {
      setExporting(false);
    }
  };

  // Bank-Location-based 3% foreign-txn fee recompute for existing
  // draft/pending reports. Preview first (no writes), confirm the counts
  // with the admin, then run for real and refresh the list.
  const handleRecomputeFees = async () => {
    setRecomputingFees(true);
    try {
      const preview = await adminExpenseApi.recomputeFees(false);
      if (preview.reportsChanged === 0) {
        toast.success('Everything is already up to date — no fees need recomputing.');
        return;
      }
      const proceed = window.confirm(
        `Recompute FX fees will update ${preview.reportsChanged} report${preview.reportsChanged !== 1 ? 's' : ''} ` +
        `(${preview.lineItemsChanged} line item${preview.lineItemsChanged !== 1 ? 's' : ''}) out of ${preview.scanned} scanned ` +
        `draft/pending reports. Only rows whose fee actually changes are written. Continue?`
      );
      if (!proceed) return;

      const tid = toast.loading('Recomputing FX fees…');
      const result = await adminExpenseApi.recomputeFees(true);
      toast.success(
        `Recomputed ${result.reportsUpdated} report${result.reportsUpdated !== 1 ? 's' : ''} ` +
        `(${result.lineItemsUpdated} line item${result.lineItemsUpdated !== 1 ? 's' : ''})` +
        (result.errorCount ? ` — ${result.errorCount} error(s), see logs` : ''),
        { id: tid }
      );
      await refresh();
    } catch (err) {
      toast.error(`Recompute failed: ${err.message}`);
    } finally {
      setRecomputingFees(false);
    }
  };

  const handleSave = async () => {
    if (!selected || !edit) return;
    setBusy(true);
    try {
      const updates = {};
      if (edit.claimRef !== selected.claimRef) updates.claimRef = edit.claimRef;
      if (edit.claimPurpose !== selected.claimPurpose) updates.claimPurpose = edit.claimPurpose;
      if (edit.officeLocation !== selected.officeLocation) updates.officeLocation = edit.officeLocation;
      if (edit.position !== selected.position) updates.position = edit.position;
      if (edit.project !== selected.project) updates.project = edit.project;
      if (edit.client !== selected.client) updates.client = edit.client;
      const editTotalRounded = Math.round(Number(edit.totalClaimedSAR) * 100) / 100;
      if (editTotalRounded !== Number(selected.totalClaimedSAR)) updates.totalClaimedSAR = editTotalRounded;
      const editAdvancesRounded = Math.round(Number(edit.lessAdvancesPaid) * 100) / 100;
      if (editAdvancesRounded !== Number(selected.lessAdvancesPaid || 0)) updates.lessAdvancesPaid = editAdvancesRounded;
      if (edit.status !== selected.status) updates.status = edit.status;

      if (Object.keys(updates).length === 0) {
        toast.info?.('No changes to save') || toast.success('No changes to save');
        return;
      }
      await adminExpenseApi.updateExpenseReport(selected.partitionKey, selected.rowKey, updates);
      toast.success('Expense report updated');
      setSelected(null);
      await refresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await adminExpenseApi.deleteExpenseReport(selected.partitionKey, selected.rowKey);
      toast.success('Expense report deleted');
      setSelected(null);
      await refresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await adminExpenseApi.markPaid(selected.partitionKey, selected.rowKey);
      toast.success('Expense report marked as paid');
      setSelected(null);
      await refresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleRestart = async (fromStep) => {
    if (!selected) return;
    setBusy(true);
    try {
      await adminExpenseApi.restartExpenseChain(selected.partitionKey, selected.rowKey, fromStep);
      toast.success(fromStep === 0 ? 'Approval chain restarted' : `Approval chain restarted from step ${fromStep + 1}`);
      setSelected(null);
      await refresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  // ── Line-item editing (Finance/admin correction of amounts) ──
  // Enter edit mode with a mutable copy of the loaded line items. Every
  // field is carried through so hidden ones (itemKey, companyCardUsed,
  // vendorName, chargeAccount, projectPermitted, expenseTypeId) survive the
  // save — the server deletes + recreates the whole set from what we post.
  const startEditLines = () => {
    const src = fullReport?.lineItems || [];
    setEditLines(
      src.map((it, i) => ({
        ...it,
        lineNumber: it.lineNumber || i + 1,
        currencyType: it.currencyType || 'SAR',
        currencyAmount: it.currencyAmount ?? 0,
        exchangeRate: it.exchangeRate ?? 1,
        date: it.date || '',
        description: it.description || '',
      }))
    );
  };

  const setLine = (idx, patch) =>
    setEditLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const addLine = () =>
    setEditLines((prev) => [
      ...prev,
      {
        lineNumber: (prev.reduce((m, l) => Math.max(m, l.lineNumber || 0), 0) || 0) + 1,
        itemKey: '',
        date: '',
        expenseTypeId: '',
        vendorName: '',
        description: '',
        currencyType: 'SAR',
        currencyAmount: 0,
        exchangeRate: 1,
        projectPermitted: false,
        companyCardUsed: false,
        chargeAccount: '',
      },
    ]);

  const removeLine = (idx) => setEditLines((prev) => prev.filter((_, i) => i !== idx));

  const handleSaveLines = async () => {
    if (!selected || !editLines) return;
    // Basic guard: each row needs a numeric amount. Reject NaN before we
    // ship garbage the server would coerce to 0.
    for (const [i, l] of editLines.entries()) {
      if (l.currencyAmount === '' || Number.isNaN(Number(l.currencyAmount))) {
        toast.error(`Line ${i + 1}: amount must be a number`);
        return;
      }
    }
    setSavingLines(true);
    try {
      const items = editLines.map((l, i) => ({
        ...l,
        lineNumber: l.lineNumber || i + 1,
        currencyAmount: Number(l.currencyAmount) || 0,
        exchangeRate: Number(l.exchangeRate) || 1,
        currencyType: (l.currencyType || 'SAR').toUpperCase(),
      }));
      const res = await adminExpenseApi.updateExpenseLineItems(selected.partitionKey, selected.rowKey, items);
      toast.success('Line items updated');
      // Reflect the server-authoritative recompute locally without a full
      // reload of the modal: refresh the full payload + the list totals.
      setEditLines(null);
      setFullReport((prev) => (prev ? { ...prev, lineItems: res.lineItems || [] } : prev));
      setSelected((prev) =>
        prev ? { ...prev, totalClaimedSAR: res.totalClaimedSAR, netAmount: res.netAmount } : prev
      );
      // Keep the report-level edit form in sync with the recalculated total,
      // otherwise a later "Save Changes" would diff against the stale value
      // and push the old total back over the line-item-derived one.
      setEdit((prev) => (prev ? { ...prev, totalClaimedSAR: res.totalClaimedSAR } : prev));
      await refresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingLines(false);
    }
  };

  // Live per-row base-SAR preview (amount × rate). The 3% foreign fee and
  // final totals are applied server-side against the OWNER's home currency,
  // so we show base only here and let the read-only table reflect the
  // authoritative figures after save.
  const editLinesBaseTotal = (editLines || []).reduce(
    (sum, l) => sum + (Number(l.currencyAmount) || 0) * (Number(l.exchangeRate) || 1),
    0
  );

  const columns = [
    {
      key: 'claimRef',
      label: 'Ref',
      render: (r) => <span className="font-mono text-xs font-semibold text-profun-cyan">{r.claimRef}</span>,
    },
    {
      key: 'requesterName',
      label: 'Employee',
      render: (r) => (
        <div>
          <div className="font-medium">{r.requesterName}</div>
          <div className="text-xs text-muted-foreground">{r.requesterDepartment}</div>
        </div>
      ),
    },
    {
      key: 'claimPurpose',
      label: 'Purpose',
      render: (r) => <span className="truncate max-w-[200px] block">{r.claimPurpose || '—'}</span>,
    },
    {
      key: 'totalClaimedSAR',
      label: 'Total (SAR)',
      render: (r) => <span className="font-mono font-semibold">{(r.totalClaimedSAR || 0).toFixed(2)}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (r) => <Badge variant={STATUS_VARIANT[r.status] || 'default'}>{r.status}</Badge>,
    },
    {
      key: 'submittedAt',
      label: 'Submitted',
      render: (r) => {
        if (!r.submittedAt) return '—';
        return new Date(r.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      },
    },
    {
      key: 'export',
      label: '',
      render: (r) => (
        <button
          onClick={(e) => { e.stopPropagation(); handleExportBundle(r); }}
          disabled={exportingId === r.rowKey}
          className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors disabled:opacity-50 disabled:pointer-events-none"
          title="Export bundle (XLSX + receipts)"
        >
          {exportingId === r.rowKey
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <FileSpreadsheet className="h-3.5 w-3.5" />}
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Expense Requests</h1>
          <p className="text-muted-foreground">
            {reports.length} total &middot; {pendingCount} pending approval
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={handleRecomputeFees} disabled={recomputingFees}>
            {recomputingFees ? <Loader2 className="h-4 w-4 animate-spin" /> : <Coins className="h-4 w-4" />}
            Recompute FX fees (pending &amp; drafts)
          </Button>
          <Button variant="ghost" onClick={() => setShowTemplateConfig(!showTemplateConfig)}>
            <Settings2 className="h-4 w-4" />
            Export Template
          </Button>
          <Button variant="secondary" onClick={handleBulkExport} disabled={exporting}>
            <Archive className="h-4 w-4" />
            {exporting ? 'Exporting...' : 'Export All'}
          </Button>
        </div>
      </div>

      {showTemplateConfig && (
        <div className="mb-6 border border-border rounded-xl overflow-hidden">
          <div className="flex items-center justify-between bg-muted/30 px-4 py-2 border-b border-border">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Export Template Configuration</span>
            <button onClick={() => setShowTemplateConfig(false)} className="text-xs text-muted-foreground hover:text-foreground transition-colors">Close</button>
          </div>
          <div className="p-4">
            <ExpenseTemplatePage />
          </div>
        </div>
      )}

      <div className="flex gap-3 mb-5 items-end flex-wrap">
        <Input
          label="Search"
          placeholder="Name, ref, purpose..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 max-w-[300px]"
        />
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={cn(
                'rounded-md border px-3 py-1.5 text-sm transition-colors cursor-pointer',
                statusFilter === f.value
                  ? 'border-primary bg-primary/10 font-semibold text-primary'
                  : 'border-input text-muted-foreground hover:bg-muted'
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={loading}
        emptyMessage="No expense reports found."
        keyFn={(r) => r.rowKey || r.claimRef}
        onRowClick={(r) => setSelected(r)}
      />

      {selected && edit && (
        <Modal
          isOpen
          onClose={() => setSelected(null)}
          title={`Expense Report ${selected.claimRef || ''}`}
          width="min(1600px, 96vw)"
        >
          {/* Two-pane layout: review-the-form on the left, receipts on the
              right. Mirrors the employee's create-report flow visually so
              an admin reviewing a submission has the same mental map. */}
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-5">
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Employee</div>
                  <div className="font-medium">{selected.requesterName}</div>
                  <div className="text-xs text-muted-foreground">{selected.requesterEmail}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Submitted</div>
                  <div>{selected.submittedAt ? new Date(selected.submittedAt).toLocaleString() : '—'}</div>
                </div>
              </div>

              <div className="rounded-lg border border-border p-4 space-y-3">
                <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  <AlertTriangle className="h-3.5 w-3.5 text-warning" />
                  Admin Override — edits apply silently and are audited
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Claim Ref"
                    value={edit.claimRef}
                    onChange={(e) => setEdit({ ...edit, claimRef: e.target.value })}
                  />
                  <div>
                    <label className="block text-[13px] font-medium text-foreground mb-1">Status</label>
                    <select
                      className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                      value={edit.status}
                      onChange={(e) => setEdit({ ...edit, status: e.target.value })}
                    >
                      {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                <Input
                  label="Purpose"
                  value={edit.claimPurpose}
                  onChange={(e) => setEdit({ ...edit, claimPurpose: e.target.value })}
                />

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Office Location"
                    value={edit.officeLocation}
                    onChange={(e) => setEdit({ ...edit, officeLocation: e.target.value })}
                  />
                  <Input
                    label="Position"
                    value={edit.position}
                    onChange={(e) => setEdit({ ...edit, position: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Project"
                    value={edit.project}
                    onChange={(e) => setEdit({ ...edit, project: e.target.value })}
                  />
                  <Input
                    label="Client"
                    value={edit.client}
                    onChange={(e) => setEdit({ ...edit, client: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Total Claimed (SAR)"
                    type="number"
                    step="0.01"
                    min="0"
                    value={edit.totalClaimedSAR}
                    onChange={(e) => setEdit({ ...edit, totalClaimedSAR: e.target.value })}
                  />
                  <Input
                    label="Less Advances Paid (SAR)"
                    type="number"
                    step="0.01"
                    min="0"
                    value={edit.lessAdvancesPaid}
                    onChange={(e) => setEdit({ ...edit, lessAdvancesPaid: e.target.value })}
                  />
                </div>

                <div className="flex justify-between gap-2 pt-1 flex-wrap">
                  {!confirmDelete ? (
                    <Button
                      variant="ghost"
                      onClick={() => setConfirmDelete(true)}
                      disabled={busy}
                      className="text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4" /> Delete report
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-destructive font-medium">Confirm permanent delete?</span>
                      <Button variant="danger" onClick={handleDelete} loading={busy}>
                        <Trash2 className="h-4 w-4" /> Yes, delete
                      </Button>
                      <Button variant="ghost" onClick={() => setConfirmDelete(false)} disabled={busy}>Cancel</Button>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    {selected.status === 'approved' && (
                      <Button
                        variant="secondary"
                        onClick={handleMarkPaid}
                        loading={busy}
                        disabled={busy}
                        className="border-transparent bg-profun-blue text-white hover:bg-profun-blue/90"
                      >
                        <BadgeCheck className="h-4 w-4" /> Mark as paid
                      </Button>
                    )}
                    <Button onClick={handleSave} loading={busy} disabled={busy}>
                      <Save className="h-4 w-4" /> Save Changes
                    </Button>
                  </div>
                </div>
              </div>

              {/* Approval chain — reuses the same restart-from-here pattern as
                  LeaveRequestsPage so admins have one mental model for both. */}
              {selected.chainSnapshot?.length > 0 && (
                <div className="rounded-lg border border-border p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Approval Chain</div>
                    <Button variant="secondary" size="sm" onClick={() => handleRestart(0)} disabled={busy}>
                      <RotateCcw className="h-3.5 w-3.5" /> Restart from start
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {selected.chainSnapshot.map((step, i) => {
                      const isDecided = step.status === 'approved' || step.status === 'rejected';
                      return (
                      <div key={i} className="flex items-center justify-between gap-2 text-xs rounded-md bg-muted/30 px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-mono text-muted-foreground">{i + 1}.</span>
                          <span className="truncate">{step.label || step.email || step.value}</span>
                          {step.status === 'approved' && <Badge variant="success">Approved</Badge>}
                          {step.status === 'rejected' && <Badge variant="danger">Rejected</Badge>}
                          {step.status === 'pending' && <Badge variant="warning">Pending</Badge>}
                          {isDecided && step.decidedBy && (
                            <span className="truncate text-muted-foreground">
                              — {step.decidedBy}
                              {step.decidedAt ? ` · ${new Date(step.decidedAt).toLocaleString()}` : ''}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => handleRestart(i)}
                          disabled={busy}
                          className="flex-shrink-0 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-background hover:border-primary/40 transition-colors cursor-pointer disabled:opacity-50"
                          title={`Restart chain from step ${i + 1}`}
                        >
                          Restart from here
                        </button>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Line items — populated by getExpenseReport, not the list endpoint. */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold">Line Items</h4>
                  {!loadingFull && editLines === null && (
                    selected.status === 'paid' ? (
                      <span className="text-[11px] text-muted-foreground">Paid — change status to edit amounts</span>
                    ) : (
                      <Button variant="secondary" size="sm" onClick={startEditLines} disabled={busy}>
                        <Pencil className="h-3.5 w-3.5" /> Edit line items
                      </Button>
                    )
                  )}
                </div>

                {loadingFull ? (
                  <p className="text-sm text-muted-foreground">Loading line items…</p>
                ) : editLines !== null ? (
                  /* ── Edit mode: correct amounts, currency, FX rate ── */
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      <AlertTriangle className="h-3.5 w-3.5 text-warning" />
                      Editing amounts — the 3% fee &amp; totals recalculate on save
                    </div>
                    <div className="overflow-x-auto rounded-lg border border-border">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border bg-muted/50 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                            <th className="px-2 py-2 w-8">#</th>
                            <th className="px-2 py-2 w-[130px]">Date</th>
                            <th className="px-2 py-2">Description</th>
                            <th className="px-2 py-2 w-[150px]">Currency</th>
                            <th className="px-2 py-2 w-[110px] text-right">Amount</th>
                            <th className="px-2 py-2 w-[100px] text-right">FX Rate</th>
                            <th className="px-2 py-2 w-[110px] text-right">Base SAR</th>
                            <th className="px-2 py-2 w-8"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {editLines.map((l, i) => {
                            const baseSar = (Number(l.currencyAmount) || 0) * (Number(l.exchangeRate) || 1);
                            return (
                              <tr key={i} className="border-b border-border/50 last:border-0 align-top">
                                <td className="px-2 py-2 text-muted-foreground">{i + 1}</td>
                                <td className="px-2 py-2">
                                  <Input
                                    type="date"
                                    value={l.date || ''}
                                    onChange={(e) => setLine(i, { date: e.target.value })}
                                  />
                                </td>
                                <td className="px-2 py-2">
                                  <Input
                                    value={l.description || ''}
                                    placeholder="Description"
                                    onChange={(e) => setLine(i, { description: e.target.value })}
                                  />
                                </td>
                                <td className="px-2 py-2">
                                  <CurrencyCombobox
                                    label=""
                                    value={l.currencyType || 'SAR'}
                                    onChange={(code) => setLine(i, { currencyType: code })}
                                  />
                                </td>
                                <td className="px-2 py-2">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    className="text-right"
                                    value={l.currencyAmount}
                                    onChange={(e) => setLine(i, { currencyAmount: e.target.value })}
                                  />
                                </td>
                                <td className="px-2 py-2">
                                  <Input
                                    type="number"
                                    step="0.0001"
                                    min="0"
                                    className="text-right"
                                    value={l.exchangeRate}
                                    onChange={(e) => setLine(i, { exchangeRate: e.target.value })}
                                  />
                                </td>
                                <td className="px-2 py-2 text-right font-mono text-muted-foreground">
                                  {baseSar.toFixed(2)}
                                </td>
                                <td className="px-2 py-2">
                                  <button
                                    type="button"
                                    onClick={() => removeLine(i)}
                                    className="text-muted-foreground hover:text-destructive"
                                    title="Remove line"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                          {editLines.length === 0 && (
                            <tr>
                              <td colSpan={8} className="px-3 py-4 text-center text-sm text-muted-foreground">
                                No line items. Add one below.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <Button variant="ghost" size="sm" onClick={addLine} disabled={savingLines}>
                        <Plus className="h-4 w-4" /> Add line
                      </Button>
                      <div className="text-xs text-muted-foreground">
                        Base total: <span className="font-mono font-semibold text-foreground">{editLinesBaseTotal.toFixed(2)} SAR</span>
                        <span className="ml-1">(+3% on foreign lines applied on save)</span>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                      <Button variant="ghost" onClick={() => setEditLines(null)} disabled={savingLines}>
                        Cancel
                      </Button>
                      <Button onClick={handleSaveLines} loading={savingLines} disabled={savingLines}>
                        <Save className="h-4 w-4" /> Save line items
                      </Button>
                    </div>
                  </div>
                ) : fullReport?.lineItems?.length > 0 ? (
                  <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border bg-muted/50">
                          <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-muted-foreground">Date</th>
                          <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-muted-foreground">Type</th>
                          <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-muted-foreground">Description</th>
                          <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-muted-foreground">Amount</th>
                          <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-muted-foreground">SAR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fullReport.lineItems.map((item, i) => (
                          <tr key={i} className="border-b border-border/50 last:border-0">
                            <td className="px-3 py-2">{item.date || item.expenseDate || '—'}</td>
                            <td className="px-3 py-2">{item.expenseTypeId || item.expenseType || '—'}</td>
                            <td className="px-3 py-2 max-w-[200px] truncate">{item.description || '—'}</td>
                            <td className="px-3 py-2 text-right font-mono">{item.currencyType || ''} {parseFloat(item.currencyAmount || item.amount || 0).toFixed(2)}</td>
                            <td className="px-3 py-2 text-right">
                              <div className="font-mono font-semibold text-profun-cyan">{parseFloat(item.sarAmount || item.amount || 0).toFixed(2)}</div>
                              {fxNote(item) && (
                                <div className="mt-0.5 inline-flex items-center rounded-full bg-profun-cyan/10 px-1.5 py-0.5 text-[10px] font-medium text-profun-cyan whitespace-nowrap">{fxNote(item)}</div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No line items on this report.</p>
                )}
              </div>

              {/* Totals */}
              <div className="bg-muted/50 rounded-lg p-4 flex justify-between items-center">
                <span className="text-sm font-medium text-muted-foreground">Total Claimed (SAR)</span>
                <span className="text-lg font-bold font-mono text-profun-cyan">
                  {(selected.totalClaimedSAR || 0).toFixed(2)}
                </span>
              </div>

              <div className="border-t border-border pt-3 flex justify-end">
                <Button
                  variant="secondary"
                  onClick={() => handleExportBundle(selected)}
                  loading={exportingId === selected.rowKey}
                >
                  <Download className="h-4 w-4" /> Export bundle (XLSX + receipts)
                </Button>
              </div>
            </div>

            {/* Right column: receipts. The list above the preview lets the
                admin flip between attachments while comparing each one to
                the values entered on the left. */}
            <div className="lg:sticky lg:top-0 lg:self-start">
              <h4 className="text-sm font-semibold mb-3">
                Receipts {fullReport?.receipts ? `(${fullReport.receipts.length})` : ''}
              </h4>
              {loadingFull ? (
                <div className="flex h-[400px] items-center justify-center rounded-lg border border-dashed border-border bg-background/40 text-sm text-muted-foreground">
                  Loading receipts…
                </div>
              ) : (
                <ReceiptViewer
                  receipts={fullReport?.receipts || []}
                  emptyMessage="No receipts attached to this report."
                />
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
